function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { CssContainer, Container } from "../../components/container";
import { PageHeader, Ribbon, Card, Pill } from "../../components/analytics-kit";
import { Link } from "react-router-dom";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Auth from "../../functions/authenticate";
import Chart from "../../components/chart";
import { hasTenantFeature, campaignMoneyVisible } from "../../functions/tenant-features";
import endpoints from "../../endpoints";
import { AuthBase } from "../../../plunk/service/auth-base";
import account from "../../data/account";
import { Button } from "@progress/kendo-react-buttons";
import { adSvc, parseName, channelLabel, num, usd, pct, budgetUsd, budgetDollars, isActiveStatus, setCampaignStatus, setCampaignBudget, setCampaignName, removeCampaign, patchCampaignCaches, rangeQs, DEFAULT_RANGE } from "./common";
import RangePicker from "../../components/range-picker";
import { RANGES, rangeLabelFor } from "../sales/territory-common";
import ServiceFactory from "../../service";

// Content Hub cross-link (CONTENT_CAMPAIGN_INTEGRATION.md §5, Phase C):
// content linked to this campaign, with downstream performance.
function contentSvc(api) {
  const Service = ServiceFactory.getType(`/content${api}`, "product"); // /v1 root
  return new Service();
}
import { getGlobalRange, subscribePeriod, unsubscribePeriod } from "../../functions/period-store";

/*
 * Campaign detail — Phase A reads + Phase B writes (AD_CAMPAIGNS spec §3).
 * Header decomposes the generated name into Flight / Channel / Market
 * chips (the raw name is technical small-print); daily trend from the
 * per-day stat rows; Ads tab grouped by ad group with the creative text
 * expanded on click. §2.5: spend/budget render only for self-managed
 * tenants (budget EDIT likewise). §2.6: no provider names anywhere.
 * Phase B: pause/activate for everyone with the section; both writes are
 * QUEUED (SQS → worker → ad platform) so the UI flips optimistically and
 * labels the change as syncing. Delete stays Phase C (today's DELETE
 * leaves the provider spending).
 */

function safeParse(v, fallback) {
  try {
    return typeof v === "string" ? JSON.parse(v) : v || fallback;
  } catch (e) {
    return fallback;
  }
}
class CampaignDetailViewComponent extends React.Component {
  constructor(props) {
    super(props);
    // Rename the campaign (the tenant's own label — local write).
    _defineProperty(this, "saveName", () => {
      const c = this.state.campaign;
      const name = (this.state.nameDraft || "").trim();
      if (!c || !name || name === c.name) {
        this.setState({
          editingName: false
        });
        return;
      }
      this.setState({
        nameBusy: true,
        writeError: null
      });
      setCampaignName(this.id(), name).then(() => {
        if (!this._alive) return;
        this.setState({
          campaign: {
            ...c,
            name
          },
          editingName: false,
          nameBusy: false
        });
        try {
          patchCampaignCaches(account.get().tenantId, this.id(), {
            name
          });
        } catch (e) {/* cache best-effort */}
      }).catch(e => {
        if (this._alive) this.setState({
          nameBusy: false,
          writeError: e.message || "Rename failed."
        });
      });
    });
    // Phase B: pause/activate. Optimistic flip + "queued" note — the write
    // enqueues to the worker; the ad platform syncs behind it.
    _defineProperty(this, "toggleStatus", async () => {
      const c = this.state.campaign;
      if (!c || this.state.statusBusy) return;
      const next = isActiveStatus(c.status) ? "Paused" : "Enabled";
      this.setState({
        statusBusy: true,
        writeError: null
      });
      try {
        await setCampaignStatus(c.id, next);
        if (!this._alive) return;
        const tenantId = (account.get() || {}).tenantId || "";
        patchCampaignCaches(tenantId, c.id, {
          status: next
        });
        this.setState({
          campaign: {
            ...c,
            status: next
          },
          statusBusy: false,
          statusQueued: true
        });
      } catch (e) {
        if (this._alive) this.setState({
          statusBusy: false,
          writeError: "The status change could not be queued — try again."
        });
      }
    });
    _defineProperty(this, "deleteCampaign", async () => {
      const c = this.state.campaign;
      if (!c || this.state.deleteBusy) return;
      this.setState({
        deleteBusy: true,
        writeError: null
      });
      try {
        await removeCampaign(c.id);
        if (!this._alive) return;
        const tenantId = (account.get() || {}).tenantId || "";
        patchCampaignCaches(tenantId, c.id, {
          status: "Removed"
        });
        this.setState({
          deleteBusy: false,
          confirmDelete: false,
          deletedNow: true,
          campaign: {
            ...c,
            status: "Removed"
          }
        });
      } catch (e) {
        if (this._alive) this.setState({
          deleteBusy: false,
          writeError: "The campaign could not be deleted — try again."
        });
      }
    });
    _defineProperty(this, "saveBudget", async () => {
      const c = this.state.campaign;
      const dollars = parseFloat(this.state.budgetInput);
      if (!c || this.state.budgetBusy) return;
      if (!(dollars > 0)) {
        this.setState({
          writeError: "Enter a daily budget above $0."
        });
        return;
      }
      const current = budgetDollars(c.budget, c.provider);
      // A big jump is almost always a typo; make the person say so.
      if (current && dollars > current * 3 && !window.confirm(`Raise this campaign's daily budget from $${current.toFixed(2)} to $${dollars.toFixed(2)} a day?`)) return;
      const micros = Math.round(dollars * 100) * 10000;
      this.setState({
        budgetBusy: true,
        writeError: null
      });
      try {
        await setCampaignBudget(c.id, micros);
        if (!this._alive) return;
        // Stored in the provider's unit (Facebook cents), as the worker writes it.
        const facebook = String(c.provider || "").toLowerCase() === "facebook";
        const budget = {
          ...(c.budget || {}),
          amount: String(facebook ? Math.round(dollars * 100) : micros)
        };
        patchCampaignCaches((account.get() || {}).tenantId || "", c.id, {
          budget
        });
        this.setState({
          campaign: {
            ...c,
            budget
          },
          budgetBusy: false,
          budgetOpen: false,
          budgetQueued: true
        });
      } catch (e) {
        let msg = "The budget change could not be queued — try again.";
        try {
          const b = JSON.parse(e.message);
          if (b && b.error) msg = b.error;
        } catch (x) {}
        if (this._alive) this.setState({
          budgetBusy: false,
          writeError: msg
        });
      }
    });
    this.state = {
      campaign: null,
      stats: [],
      assets: [],
      error: false,
      expandedAd: null,
      range: getGlobalRange(),
      adSort: {
        key: "impressions",
        dir: -1
      },
      showAllZips: false,
      flightId: null,
      statusBusy: false,
      statusQueued: false,
      budgetOpen: false,
      budgetInput: "",
      budgetBusy: false,
      budgetQueued: false,
      writeError: null,
      editingName: false,
      nameDraft: "",
      nameBusy: false,
      content: null,
      confirmDelete: false,
      deleteBusy: false,
      deletedNow: false
    };
    this._alive = true;
  }
  id() {
    return this.props.params.id;
  }
  async componentDidMount() {
    if (!(Auth.isAuthenticated() || (await Auth.attachOrSignIn()))) return;
    this.load(this.state.range);
    // Range lives in component state (not the URL) — the global period
    // control reloads in place, no navigation.
    this._periodTok = subscribePeriod(({
      range
    }) => {
      if (!this._alive || !range || range === this.state.range) return;
      this.setState({
        range
      });
      this.load(range);
    });
    // §213: link back to the flight — wizard-created campaigns match a
    // /flights row by event name; legacy campaigns have none (no link).
    adSvc("/flights").getAsync().then(rows => {
      if (!this._alive || !this.state.campaign) return;
      const flightName = parseName((this.state.campaign || {}).name).flight.toLowerCase();
      const hit = (Array.isArray(rows) ? rows : []).find(f => String(f.eventName || f.name || "").replace(/_/g, " ").toLowerCase() === flightName);
      if (hit) this.setState({
        flightId: String(hit.id)
      });
    }).catch(() => {/* no flights — no link */});
  }

  // §213/§215: campaign KPIs, daily rows, and per-ad metrics all window
  // server-side (?range= / ?from=&to= — probed live).
  async load(range) {
    this._loadSeq = (this._loadSeq || 0) + 1;
    const seq = this._loadSeq;
    try {
      const [campaign, stats, assets] = await Promise.all([adSvc(`/campaigns/${this.id()}?${rangeQs(range)}`).getAsync(), adSvc(`/stats/campaign/${this.id()}?${rangeQs(range)}`).getAsync().catch(() => []), adSvc(`/campaigns/${this.id()}/assets?${rangeQs(range)}`).getAsync().catch(() => [])]);
      if (this._alive && seq === this._loadSeq) this.setState({
        campaign,
        stats: stats || [],
        assets: assets || []
      });
      // Content Hub cross-link: content linked to this campaign (by id and
      // by parsed name so both link kinds match). Renders only if matched.
      if (campaign) {
        const refs = [String(campaign.id || ""), campaign.name || "", parseName(campaign.name).flight || ""].filter(Boolean).join(",");
        const cr = await contentSvc(`/for-campaign?refs=${encodeURIComponent(refs)}&range=this_quarter`).getAsync().catch(() => null);
        if (this._alive && seq === this._loadSeq) this.setState({
          content: cr && cr.items || []
        });
      }
    } catch (e) {
      if (this._alive && seq === this._loadSeq) this.setState({
        error: true
      });
    }
  }
  componentWillUnmount() {
    this._alive = false;
    unsubscribePeriod(this._periodTok);
  }
  videoViewRate() {
    const rows = (this.state.stats || []).filter(s => Number(s.videoViews) > 0 && s.videoViewRate != null);
    const views = rows.reduce((a, s) => a + (Number(s.videoViews) || 0), 0);
    if (!views) return null;
    const weighted = rows.reduce((a, s) => a + (Number(s.videoViewRate) || 0) * (Number(s.videoViews) || 0), 0) / views;
    // the API's rate is a fraction-as-string ("0.174…") → percent
    return weighted <= 1 ? weighted * 100 : weighted;
  }
  trendData(moneyVisible) {
    const rows = (this.state.stats || []).filter(s => s.rangeType === "DAY").sort((a, b) => new Date(a.date) - new Date(b.date));
    if (rows.length < 2) return null;
    const head = moneyVisible ? ["Day", "Impressions", "Clicks", "Spend"] : ["Day", "Impressions", "Clicks"];
    const data = [head];
    // §215: every day labeled = an unreadable smear on long windows.
    // Thin to ~12 visible labels; the bars keep their per-day values.
    const step = Math.max(1, Math.ceil(rows.length / 12));
    rows.forEach((s, i) => {
      const d = new Date(s.date);
      const label = i % step === 0 ? `${d.getMonth() + 1}/${d.getDate()}` : " ".repeat(i % step + 1);
      const base = [label, Number(s.impressions) || 0, Number(s.clicks) || 0];
      data.push(moneyVisible ? [...base, Math.round((Number(s.spend) || 0) * 100) / 100] : base);
    });
    return data;
  }
  render() {
    if (!hasTenantFeature("campaigns")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Ad Campaigns isn't configured for this workspace."));
    }
    const {
      campaign: c,
      assets,
      error,
      expandedAd
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, {
      message: "This campaign could not be loaded."
    });
    if (!c) return /*#__PURE__*/React.createElement(Loader, null);
    const moneyVisible = campaignMoneyVisible();
    const p = parseName(c.name);
    const m = c.metrics || {};
    const targeting = c.targeting || {};
    const locations = targeting.locations || [];
    const chartData = this.trendData(moneyVisible);
    const groups = new Map();
    (assets || []).forEach(a => {
      const g = String(a.adGroup || "").split("_").pop() || "ads";
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g).push(a);
    });
    return /*#__PURE__*/React.createElement("div", {
      className: "territory-view wa-view campaigns-view campaign-detail"
    }, /*#__PURE__*/React.createElement(PageHeader, {
      crumbs: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Link, {
        to: "/Campaigns"
      }, "Campaigns"), " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: "/Campaigns/Flights"
      }, "Flights"), this.state.flightId ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement(Link, {
        to: `/Campaigns/flight/${this.state.flightId}`
      }, p.flight)) : null, " \xB7 Campaign"),
      title: this.state.editingName ? /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          alignItems: "center",
          gap: 6
        }
      }, /*#__PURE__*/React.createElement("input", {
        autoFocus: true,
        value: this.state.nameDraft,
        onChange: e => this.setState({
          nameDraft: e.target.value
        }),
        onKeyDown: e => {
          if (e.key === "Enter") this.saveName();
          if (e.key === "Escape") this.setState({
            editingName: false
          });
        },
        style: {
          fontSize: 15,
          padding: "6px 8px",
          minWidth: 280,
          border: "1px solid var(--plk-border, #dce0e5)",
          borderRadius: 8,
          fontWeight: 400
        }
      }), /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: this.state.nameBusy,
        onClick: this.saveName
      }, this.state.nameBusy ? "Saving…" : "Save"), /*#__PURE__*/React.createElement(Button, {
        disabled: this.state.nameBusy,
        onClick: () => this.setState({
          editingName: false
        })
      }, "Cancel")) : /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          alignItems: "center",
          gap: 6
        }
      }, p.flight, /*#__PURE__*/React.createElement("button", {
        className: "territory-back no-border",
        title: "Rename campaign",
        onClick: () => this.setState({
          editingName: true,
          nameDraft: c.name
        })
      }, /*#__PURE__*/React.createElement("span", {
        className: "material-icons",
        style: {
          fontSize: 16
        }
      }, "edit"))),
      subtitle: `${channelLabel(c.type)}${p.market ? ` · ${p.market}` : ""} · ${rangeLabelFor(this.state.range, RANGES) || ""} · stats refresh about every 4 hours`,
      chips: [{
        k: "Channel",
        v: channelLabel(c.type)
      }, p.market ? {
        k: "Market",
        v: p.market
      } : null, {
        k: "Status",
        v: isActiveStatus(c.status) ? "Active" : "Paused"
      }],
      actions: /*#__PURE__*/React.createElement(React.Fragment, null, this.state.flightId && /*#__PURE__*/React.createElement(Button, {
        onClick: () => this.props.history.push(`/Campaigns/flight/${this.state.flightId}`),
        title: "Open this campaign's flight"
      }, "View flight"), moneyVisible && /*#__PURE__*/React.createElement(Button, {
        onClick: () => this.props.history.push(`/Campaigns/EditCampaign/${this.id()}`)
      }, "Edit campaign"), /*#__PURE__*/React.createElement(Button, {
        disabled: this.state.statusBusy,
        onClick: this.toggleStatus
      }, this.state.statusBusy ? "Queuing…" : isActiveStatus(c.status) ? "Pause campaign" : "Activate campaign"), moneyVisible && (this.state.deletedNow ? /*#__PURE__*/React.createElement("span", {
        className: "cust-stage",
        style: {
          background: "var(--plk-surface-alt)",
          color: "var(--plk-text-dim)"
        }
      }, "Deleting\u2026") : this.state.confirmDelete ? /*#__PURE__*/React.createElement("span", {
        style: {
          display: "inline-flex",
          gap: 6,
          alignItems: "center"
        }
      }, /*#__PURE__*/React.createElement(Button, {
        disabled: this.state.deleteBusy,
        themeColor: "error",
        onClick: this.deleteCampaign,
        title: "Removes this campaign at the ad platform \u2014 this cannot be undone"
      }, this.state.deleteBusy ? "Deleting…" : "Confirm delete"), /*#__PURE__*/React.createElement(Button, {
        disabled: this.state.deleteBusy,
        onClick: () => this.setState({
          confirmDelete: false
        })
      }, "Cancel")) : /*#__PURE__*/React.createElement(Button, {
        onClick: () => this.setState({
          confirmDelete: true
        }),
        title: "Delete this campaign \u2014 removes it at the ad platform"
      }, "Delete campaign")))
    }), this.state.deletedNow && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, "Delete queued: the campaign is being removed at the ad platform and drops to Removed within a few minutes."), (this.state.statusQueued || this.state.budgetQueued) && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice"
    }, "Change queued: it syncs to the ad platform within a few minutes; stats keep their 4-hour cadence."), this.state.writeError && /*#__PURE__*/React.createElement("div", {
      className: "wa-notice danger"
    }, this.state.writeError), /*#__PURE__*/React.createElement(Ribbon, {
      comparing: null,
      tiles: [{
        l: "Impressions",
        v: num(m.impressions),
        s: Number(m.videoViews) > 0 ? `${num(m.videoViews)} video views${this.videoViewRate() != null ? ` · ${pct(this.videoViewRate())} view rate` : ""}` : null
      }, {
        l: "Clicks",
        v: num(m.clicks),
        s: `${pct(m.ctr)} CTR`
      }, {
        l: "Conversions",
        v: num(m.conversions)
      }, moneyVisible ? {
        l: "Spend",
        v: usd(m.spend),
        s: budgetUsd(c.budget, c.provider) ? `${budgetUsd(c.budget, c.provider)} daily budget` : null
      } : {
        l: "CTR",
        v: pct(m.ctr)
      }],
      note: moneyVisible && c.budget ?
      /*#__PURE__*/
      // Editable whenever a budget object exists; legacy rows with unverifiable units just prefill blank.
      React.createElement("a", {
        style: {
          cursor: "pointer",
          color: "var(--plk-accent, #ff6358)"
        },
        onClick: () => {
          const d = budgetDollars(c.budget, c.provider);
          this.setState({
            budgetOpen: !this.state.budgetOpen,
            budgetInput: d == null ? "" : String(Number(d.toFixed(2)))
          });
        }
      }, "Edit daily budget") : null
    }), /*#__PURE__*/React.createElement(CssContainer, {
      cls: "margin-squeeze"
    }, this.state.budgetOpen && moneyVisible ? /*#__PURE__*/React.createElement("div", {
      style: {
        display: "flex",
        gap: "8px",
        alignItems: "center",
        margin: "8px 5px 16px"
      }
    }, /*#__PURE__*/React.createElement("span", {
      style: {
        fontSize: "13px",
        lineHeight: 1,
        margin: 0
      }
    }, "Daily budget $"), /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      step: "1",
      value: this.state.budgetInput,
      onChange: e => this.setState({
        budgetInput: e.target.value
      }),
      style: {
        width: "110px",
        height: "36px",
        margin: 0,
        padding: "0 8px",
        boxSizing: "border-box",
        border: "1px solid var(--plk-border)",
        borderRadius: "6px",
        fontSize: "13px"
      }
    }), /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      disabled: this.state.budgetBusy,
      onClick: this.saveBudget
    }, this.state.budgetBusy ? "Queuing…" : "Save"), /*#__PURE__*/React.createElement(Button, {
      onClick: () => this.setState({
        budgetOpen: false
      })
    }, "Cancel")) : null), /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, chartData && /*#__PURE__*/React.createElement(Card, {
      title: "Daily performance",
      hint: "impressions, clicks and spend by day"
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-trend"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "300px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chartData,
      options: {
        seriesType: "bars",
        series: moneyVisible ? {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358",
            tooltipFormat: "{0:n0}"
          },
          1: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237",
            tooltipFormat: "{0:n0}"
          },
          2: {
            type: "line",
            targetAxisIndex: 1,
            color: "#aa46be",
            lineDashStyle: [4, 3],
            tooltipFormat: "currency"
          }
        } : {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358",
            tooltipFormat: "{0:n0}"
          },
          1: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237",
            tooltipFormat: "{0:n0}"
          }
        },
        vAxes: {
          0: {
            title: "Impressions",
            format: "short"
          },
          1: {
            title: moneyVisible ? "Clicks / Spend" : "Clicks",
            format: "0"
          }
        },
        legend: {
          position: "top"
        },
        backgroundColor: "transparent",
        chartArea: {
          left: 70,
          right: 60,
          top: 40,
          bottom: 40
        }
      }
    }))), /*#__PURE__*/React.createElement(Card, {
      title: "Targeting",
      span: (this.state.content || []).length > 0 ? 6 : 12
    }, /*#__PURE__*/React.createElement("div", {
      className: "flex",
      style: {
        display: "flex",
        gap: "40px",
        flexWrap: "wrap",
        fontSize: "13px"
      }
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      className: "mtf-dim",
      style: {
        fontSize: "10.5px",
        textTransform: "uppercase",
        letterSpacing: ".06em",
        margin: "0 0 4px"
      }
    }, "Targeted ZIP codes"), locations.length ? /*#__PURE__*/React.createElement(React.Fragment, null, (this.state.showAllZips ? locations : locations.slice(0, 14)).map(l => /*#__PURE__*/React.createElement("span", {
      key: l,
      className: "cust-stage",
      style: {
        background: "var(--plk-surface-alt)",
        color: "var(--plk-text-dim)",
        marginRight: "4px",
        marginBottom: "4px",
        display: "inline-block"
      }
    }, l)), locations.length > 14 ? /*#__PURE__*/React.createElement("a", {
      className: "mtf-dim",
      style: {
        fontSize: "12px",
        cursor: "pointer"
      },
      onClick: () => this.setState({
        showAllZips: !this.state.showAllZips
      })
    }, this.state.showAllZips ? " show fewer" : ` +${locations.length - 14} more`) : null) : "—"), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("p", {
      className: "mtf-dim",
      style: {
        fontSize: "10.5px",
        textTransform: "uppercase",
        letterSpacing: ".06em",
        margin: "0 0 4px"
      }
    }, "Languages"), (targeting.languages || []).join(", ") || "—")), !moneyVisible && /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "10px 0 0"
      }
    }, "Targeting changes are handled by your Plunk rep.")), (this.state.content || []).length > 0 && /*#__PURE__*/React.createElement(Card, {
      title: "Content used here",
      hint: "linked in the Content Hub",
      span: 6
    }, /*#__PURE__*/React.createElement("table", {
      className: "territory-table wa-table"
    }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Content"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Sessions"), /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Conversions"), moneyVisible ? /*#__PURE__*/React.createElement("th", {
      className: "n"
    }, "Sales") : null)), /*#__PURE__*/React.createElement("tbody", null, this.state.content.map(ct => /*#__PURE__*/React.createElement("tr", {
      key: String(ct.id),
      className: "clickable",
      style: {
        cursor: "pointer"
      },
      onClick: () => this.props.history.push("/Content"),
      title: "Open in the Content Hub"
    }, /*#__PURE__*/React.createElement("td", null, ct.title || `Content #${ct.id}`), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, Number(ct.sessions) > 0 ? num(ct.sessions) : "—"), /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, Number(ct.conversions) > 0 ? num(ct.conversions) : "—"), moneyVisible ? /*#__PURE__*/React.createElement("td", {
      className: "n"
    }, Number(ct.salesValue) > 0 ? usd(ct.salesValue) : "—") : null)))), /*#__PURE__*/React.createElement("p", {
      className: "territory-hint",
      style: {
        margin: "8px 0 0"
      }
    }, "Content linked to this campaign \xB7 ", /*#__PURE__*/React.createElement("a", {
      style: {
        cursor: "pointer"
      },
      onClick: () => this.props.history.push("/Content")
    }, "open the Content Hub \u2192"))), /*#__PURE__*/React.createElement(Card, {
      title: `Ads (${(assets || []).length})`
    }, (assets || []).length === 0 ? /*#__PURE__*/React.createElement("p", {
      className: "territory-hint"
    }, "No ads recorded for this campaign.") : (() => {
      // §213: conversions column only where the channel reports
      // them; sortable columns (client-side over the loaded rows).
      const anyConv = (assets || []).some(a => Number((a.metrics || {}).conversions) > 0);
      const anyVideo = (assets || []).some(a => Number((a.metrics || {}).videoViews) > 0);
      const {
        adSort
      } = this.state;
      const sortTh = (key, label) => /*#__PURE__*/React.createElement("th", {
        className: "r clickable",
        style: {
          cursor: "pointer",
          whiteSpace: "nowrap"
        },
        onClick: () => this.setState({
          adSort: {
            key,
            dir: adSort.key === key ? -adSort.dir : -1
          }
        })
      }, label, adSort.key === key ? adSort.dir === -1 ? " ▾" : " ▴" : "");
      const sortAds = ads => ads.slice().sort((a, b) => {
        const av = Number((a.metrics || {})[adSort.key]) || 0;
        const bv = Number((b.metrics || {})[adSort.key]) || 0;
        return (av - bv) * adSort.dir * -1;
      });
      return [...groups.entries()].map(([g, ads]) => /*#__PURE__*/React.createElement("div", {
        key: g,
        style: {
          marginBottom: "10px"
        }
      }, /*#__PURE__*/React.createElement("table", {
        className: "territory-table wa-table"
      }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("th", null, "Ad"), /*#__PURE__*/React.createElement("th", null, "Status"), sortTh("impressions", "Impressions"), sortTh("clicks", "Clicks"), sortTh("ctr", "CTR"), anyConv ? sortTh("conversions", "Conversions") : null, anyVideo ? sortTh("videoViews", "Video Views") : null, moneyVisible ? sortTh("spend", "Spend") : null)), /*#__PURE__*/React.createElement("tbody", null, sortAds(ads).map(a => {
        const am = a.metrics || {};
        // Display ads store an ARRAY of creatives; video ads store ONE object (ResponsiveVideoAdAsset).
        const parsedAd = safeParse(a.ad, null);
        const creative = Array.isArray(parsedAd) ? parsedAd[0] || {} : parsedAd && typeof parsedAd === "object" ? parsedAd : {};
        // §215: "Image / Image" — when there's no headline,
        // identify the ad by its creative filename instead of
        // repeating the ad type in both lines.
        const imgUrl = (creative.ImageUrls || creative.Images || [])[0] || "";
        const imgName = imgUrl ? decodeURIComponent(String(imgUrl).split("/").pop().split("?")[0]) : "";
        const videoIds = (creative.VideoUrls || creative.YouTubeVideoIds || []).map(v => String(v).replace(/^.*(?:v=|youtu\.be\/)/, "")).filter(Boolean);
        const headline = (creative.Headlines || [])[0] || (creative.LongHeadlines || [])[0] || creative.Message || imgName || (videoIds.length ? `Video ${videoIds[0]}` : "") || a.name || a.adType;
        const open = expandedAd === a.id;
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: a.id
        }, /*#__PURE__*/React.createElement("tr", {
          className: "clickable",
          onClick: () => this.setState({
            expandedAd: open ? null : a.id
          }),
          title: "Show creative"
        }, /*#__PURE__*/React.createElement("td", null, /*#__PURE__*/React.createElement("span", {
          className: "name"
        }, headline), /*#__PURE__*/React.createElement("span", {
          className: "code"
        }, a.adType)), /*#__PURE__*/React.createElement("td", null, a.status === "Enabled" ? /*#__PURE__*/React.createElement("span", {
          className: "cust-stage",
          style: {
            background: "#e5f3ec",
            color: "#217a4b"
          }
        }, "active") : /*#__PURE__*/React.createElement("span", {
          className: "cust-stage",
          style: {
            background: "var(--plk-surface-alt)",
            color: "var(--plk-text-dim)"
          }
        }, String(a.status || "").toLowerCase())), /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, num(am.impressions)), /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, num(am.clicks)), /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, pct(am.ctr)), anyConv ? /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, num(am.conversions)) : null, anyVideo ? /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, Number(am.videoViews) > 0 ? num(am.videoViews) : "—") : null, moneyVisible ? /*#__PURE__*/React.createElement("td", {
          className: "n"
        }, usd(am.spend)) : null), open ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
          colSpan: 5 + (anyConv ? 1 : 0) + (anyVideo ? 1 : 0) + (moneyVisible ? 1 : 0),
          style: {
            background: "var(--plk-surface-alt)",
            fontSize: "12.5px",
            padding: "10px 14px"
          }
        }, videoIds.length ? /*#__PURE__*/React.createElement("div", {
          style: {
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            marginBottom: "6px"
          }
        }, videoIds.map(id => /*#__PURE__*/React.createElement("a", {
          key: id,
          href: `https://www.youtube.com/watch?v=${id}`,
          target: "_blank",
          rel: "noopener noreferrer",
          title: "Open on YouTube",
          onClick: e => e.stopPropagation()
        }, /*#__PURE__*/React.createElement("img", {
          src: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
          alt: "",
          style: {
            width: 200,
            height: 112,
            objectFit: "cover",
            borderRadius: 6,
            display: "block"
          }
        })))) : null, (creative.Headlines || []).length ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Headlines:"), " ", (creative.Headlines || []).join(" · ")) : null, (creative.LongHeadlines || []).filter(Boolean).length ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Headlines:"), " ", (creative.LongHeadlines || []).filter(Boolean).join(" · ")) : null, creative.FinalUrl ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Landing page:"), " ", creative.FinalUrl) : null, creative.LongHeadline ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Long headline:"), " ", creative.LongHeadline) : null, (creative.Descriptions || []).length ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Descriptions:"), " ", (creative.Descriptions || []).join(" · ")) : null, creative.Message ? /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, "Message:"), " ", creative.Message) : null, (creative.ImageUrls || creative.Images || []).length ? /*#__PURE__*/React.createElement("div", {
          style: {
            marginTop: "6px",
            display: "flex",
            gap: "8px",
            flexWrap: "wrap"
          }
        }, (creative.ImageUrls || creative.Images || []).slice(0, 6).map((u, i) => /*#__PURE__*/React.createElement("img", {
          key: i,
          src: u,
          alt: "",
          style: {
            maxHeight: "80px",
            borderRadius: "4px"
          }
        }))) : null)) : null);
      })))));
    })())));
  }
}
export default withRouter(CampaignDetailViewComponent);